/**
 * Reading helpers and the immutable edits (add, change, remove, move, regroup,
 * connect) that keep a document valid. Every edit returns the SAME object when
 * nothing changed. Moved out of model.ts as is.
 */
import {
  FLOW_DIRECTIONS,
  FLOW_DOC_VERSION,
  FLOW_EDGE_KINDS,
  FLOW_EDGE_STYLES,
  FLOW_HANDLES,
  FLOW_LIMITS,
  FLOW_NODE_KINDS,
  FLOW_NODE_SIZE,
  FLOW_TONES,
  type FlowDirection,
  type FlowEdge,
  type FlowEdgeKind,
  type FlowHandle,
  type FlowLink,
  type FlowMapDoc,
  type FlowNode,
  type FlowNodeKind,
  type FlowPoint,
  type FlowRef,
  type FlowSize,
  type FlowStep,
  type FlowText,
  type FlowTone,
} from "../types.js";
import {
  oneOf,
  isFlowId,
  makeFlowId,
  cleanText,
  cleanOptionalText,
  coord,
  cleanPoint,
  cleanSize,
  canonicalNode,
  canonicalEdge,
  canonicalDoc,
  orderNodes,
  same,
} from "./shared.js";
import { cleanSteps, cleanLinks, cleanRef, normalizeFlowDoc } from "./normalize.js";

// ---------------------------------------------------------------------------
// Reading helpers
// ---------------------------------------------------------------------------

/** The node's top-left corner in canvas coordinates (children are stored relative to their group). */
export function absolutePosition(doc: FlowMapDoc, id: string): FlowPoint {
  const n = doc.nodes.find((x) => x.id === id);
  if (!n) return { x: 0, y: 0 };
  if (!n.parentId) return n.position;
  const p = doc.nodes.find((x) => x.id === n.parentId);
  return p ? { x: coord(p.position.x + n.position.x), y: coord(p.position.y + n.position.y) } : n.position;
}

export function childrenOf(doc: FlowMapDoc, groupId: string): FlowNode[] {
  return doc.nodes.filter((n) => n.parentId === groupId);
}

export interface FlowStats {
  nodes: number;
  groups: number;
  edges: number;
  steps: number;
  stepsDone: number;
}

export function flowStats(doc: FlowMapDoc): FlowStats {
  let groups = 0;
  let steps = 0;
  let stepsDone = 0;
  for (const n of doc.nodes) {
    if (n.kind === "group") groups++;
    for (const s of n.steps ?? []) {
      steps++;
      if (s.done) stepsDone++;
    }
  }
  return { nodes: doc.nodes.length, groups, edges: doc.edges.length, steps, stepsDone };
}

// ---------------------------------------------------------------------------
// Edits
// ---------------------------------------------------------------------------

function detach(child: FlowNode, parent: FlowNode | undefined): FlowNode {
  const next: FlowNode = { ...child };
  delete next.parentId;
  if (parent) next.position = { x: coord(parent.position.x + child.position.x), y: coord(parent.position.y + child.position.y) };
  return canonicalNode(next);
}

export interface NewNodeInput {
  id?: string;
  kind?: FlowNodeKind;
  position?: FlowPoint;
  title?: FlowText;
  description?: FlowText;
  tone?: FlowTone;
  steps?: FlowStep[];
  links?: FlowLink[];
  /** Ignored for groups, and unless it names a group. */
  parentId?: string;
  size?: FlowSize;
  ref?: FlowRef;
}

/** Add a node. `id` is null (and the document unchanged) when the map is full. */
export function addNode(doc: FlowMapDoc, input: NewNodeInput = {}, opts: { random?: boolean } = {}): { doc: FlowMapDoc; id: string | null } {
  if (doc.nodes.length >= FLOW_LIMITS.nodes) return { doc, id: null };
  const taken = new Set(doc.nodes.map((n) => n.id));
  const kind: FlowNodeKind = oneOf(FLOW_NODE_KINDS, input.kind) ? input.kind : "step";
  const id = isFlowId(input.id) && !taken.has(input.id) ? input.id : makeFlowId(kind === "group" ? "g" : "n", taken, opts.random);
  const node: FlowNode = {
    id,
    kind,
    position: cleanPoint(input.position) ?? { x: 0, y: 0 },
    title: cleanText(input.title, FLOW_LIMITS.title) ?? "",
  };
  const description = cleanOptionalText(input.description, FLOW_LIMITS.text);
  if (description !== undefined) node.description = description;
  if (oneOf(FLOW_TONES, input.tone)) node.tone = input.tone;
  const steps = cleanSteps(input.steps);
  if (steps) node.steps = steps;
  const links = cleanLinks(input.links, id, taken);
  if (links) node.links = links;
  if (kind === "group") node.size = cleanSize(input.size) ?? { ...FLOW_NODE_SIZE.group };
  else if (input.parentId && doc.nodes.some((n) => n.id === input.parentId && n.kind === "group")) node.parentId = input.parentId;
  const ref = cleanRef(input.ref);
  if (ref) node.ref = ref;
  const nodes = orderNodes([...doc.nodes, canonicalNode(node)]);
  return { doc: { ...doc, nodes }, id };
}

export interface NodePatch {
  kind?: FlowNodeKind;
  position?: FlowPoint;
  title?: FlowText;
  /** null removes the field. */
  description?: FlowText | null;
  tone?: FlowTone | null;
  steps?: FlowStep[] | null;
  links?: FlowLink[] | null;
  /** Groups only. */
  size?: FlowSize | null;
  ref?: FlowRef | null;
}

/**
 * Change one node. Group membership changes go through `setNodeParent`, which
 * also converts the position. Turning a group into another kind releases its
 * children (they keep their place on the canvas); turning a node into a group
 * takes it out of its own group.
 */
export function updateNode(doc: FlowMapDoc, id: string, patch: NodePatch): FlowMapDoc {
  const cur = doc.nodes.find((n) => n.id === id);
  if (!cur) return doc;
  const nodeIds = new Set(doc.nodes.map((n) => n.id));
  const next: FlowNode = { ...cur };
  if (oneOf(FLOW_NODE_KINDS, patch.kind)) next.kind = patch.kind;
  if (patch.position) {
    const p = cleanPoint(patch.position);
    if (p) next.position = p;
  }
  if (patch.title !== undefined) next.title = cleanText(patch.title, FLOW_LIMITS.title) ?? "";
  if (patch.description !== undefined) {
    const d = patch.description === null ? undefined : cleanOptionalText(patch.description, FLOW_LIMITS.text);
    if (d === undefined) delete next.description;
    else next.description = d;
  }
  if (patch.tone !== undefined) {
    if (patch.tone !== null && oneOf(FLOW_TONES, patch.tone)) next.tone = patch.tone;
    else delete next.tone;
  }
  if (patch.steps !== undefined) {
    const s = patch.steps === null ? undefined : cleanSteps(patch.steps);
    if (s) next.steps = s;
    else delete next.steps;
  }
  if (patch.links !== undefined) {
    const l = patch.links === null ? undefined : cleanLinks(patch.links, id, nodeIds);
    if (l) next.links = l;
    else delete next.links;
  }
  if (patch.ref !== undefined) {
    const r = patch.ref === null ? undefined : cleanRef(patch.ref);
    if (r) next.ref = r;
    else delete next.ref;
  }

  const wasGroup = cur.kind === "group";
  const isGroup = next.kind === "group";
  if (isGroup) {
    if (patch.size !== undefined && patch.size !== null) {
      const s = cleanSize(patch.size);
      if (s) next.size = s;
    }
    if (!next.size) next.size = { ...FLOW_NODE_SIZE.group };
    if (next.parentId) {
      const parent = doc.nodes.find((n) => n.id === next.parentId);
      if (parent) next.position = { x: coord(parent.position.x + next.position.x), y: coord(parent.position.y + next.position.y) };
      delete next.parentId;
    }
  } else {
    delete next.size;
  }

  const canon = canonicalNode(next);
  if (same(canon, cur)) return doc;
  let nodes = doc.nodes.map((n) => (n.id === id ? canon : n));
  if (wasGroup && !isGroup) nodes = nodes.map((n) => (n.parentId === id ? detach(n, next) : n));
  return { ...doc, nodes: orderNodes(nodes) };
}

/**
 * Remove nodes, every arrow touching them and every link jumping to them.
 * Children of a removed group stay on the canvas where they were.
 */
export function removeNodes(doc: FlowMapDoc, ids: Iterable<string>): FlowMapDoc {
  const drop = new Set(ids);
  if (!doc.nodes.some((n) => drop.has(n.id))) return doc;
  const byId = new Map(doc.nodes.map((n) => [n.id, n] as const));
  const nodes = doc.nodes
    .filter((n) => !drop.has(n.id))
    .map((n) => {
      let out = n;
      if (out.parentId && drop.has(out.parentId)) out = detach(out, byId.get(out.parentId));
      if (out.links?.some((l) => l.nodeId && drop.has(l.nodeId))) {
        const links = out.links
          .map((l) => (l.nodeId && drop.has(l.nodeId) ? (l.href ? { id: l.id, label: l.label, href: l.href } : null) : l))
          .filter((l): l is FlowLink => l !== null);
        const next: FlowNode = { ...out };
        if (links.length) next.links = links;
        else delete next.links;
        out = canonicalNode(next);
      }
      return out;
    });
  const edges = doc.edges.filter((e) => !drop.has(e.source) && !drop.has(e.target));
  return { ...doc, nodes, edges };
}

/** Move nodes. Positions are in each node's own space (relative to its group when it has one). */
export function moveNodes(doc: FlowMapDoc, moves: ReadonlyArray<{ id: string; position: FlowPoint }>): FlowMapDoc {
  const want = new Map<string, FlowPoint>();
  for (const m of moves) {
    const p = cleanPoint(m.position);
    if (p) want.set(m.id, p);
  }
  let changed = false;
  const nodes = doc.nodes.map((n) => {
    const p = want.get(n.id);
    if (!p || (p.x === n.position.x && p.y === n.position.y)) return n;
    changed = true;
    return { ...n, position: p };
  });
  return changed ? { ...doc, nodes } : doc;
}

/**
 * Put a node into a group (or take it out with `null`). The node keeps its place
 * on the canvas: its stored position switches between absolute and
 * group-relative. Groups cannot go inside groups.
 */
export function setNodeParent(doc: FlowMapDoc, id: string, parentId: string | null): FlowMapDoc {
  const node = doc.nodes.find((n) => n.id === id);
  if (!node || node.kind === "group") return doc;
  if ((node.parentId ?? null) === parentId) return doc;
  const abs = absolutePosition(doc, id);
  let next: FlowNode;
  if (parentId === null) {
    next = { ...node, position: abs };
    delete next.parentId;
  } else {
    const parent = doc.nodes.find((n) => n.id === parentId);
    if (!parent || parent.kind !== "group" || parent.id === id) return doc;
    next = { ...node, parentId, position: { x: coord(abs.x - parent.position.x), y: coord(abs.y - parent.position.y) } };
  }
  const canon = canonicalNode(next);
  return { ...doc, nodes: orderNodes(doc.nodes.map((n) => (n.id === id ? canon : n))) };
}

export interface ConnectInput {
  source: string;
  target: string;
  sourceHandle?: FlowHandle;
  targetHandle?: FlowHandle;
  label?: FlowText;
  kind?: FlowEdgeKind;
}

/**
 * Add an arrow. `id` is null (and the document unchanged) for a self loop, a
 * missing end, an arrow that already exists, or a full map.
 */
export function connectNodes(doc: FlowMapDoc, input: ConnectInput, opts: { random?: boolean } = {}): { doc: FlowMapDoc; id: string | null } {
  const { source, target } = input;
  if (source === target || doc.edges.length >= FLOW_LIMITS.edges) return { doc, id: null };
  if (!doc.nodes.some((n) => n.id === source) || !doc.nodes.some((n) => n.id === target)) return { doc, id: null };
  if (doc.edges.some((e) => e.source === source && e.target === target)) return { doc, id: null };
  const id = makeFlowId("e", new Set(doc.edges.map((e) => e.id)), opts.random);
  const e: FlowEdge = { id, source, target };
  if (oneOf(FLOW_HANDLES, input.sourceHandle)) e.sourceHandle = input.sourceHandle;
  if (oneOf(FLOW_HANDLES, input.targetHandle)) e.targetHandle = input.targetHandle;
  const label = cleanOptionalText(input.label, FLOW_LIMITS.label);
  if (label !== undefined) e.label = label;
  if (oneOf(FLOW_EDGE_KINDS, input.kind) && input.kind !== "default") e.kind = input.kind;
  return { doc: { ...doc, edges: [...doc.edges, canonicalEdge(e)] }, id };
}

export interface EdgePatch {
  source?: string;
  target?: string;
  /** null removes the field. */
  sourceHandle?: FlowHandle | null;
  targetHandle?: FlowHandle | null;
  label?: FlowText | null;
  kind?: FlowEdgeKind | null;
}

/** Change one arrow. A patch that would create a self loop, a dangling end or a duplicate is ignored. */
export function updateEdge(doc: FlowMapDoc, id: string, patch: EdgePatch): FlowMapDoc {
  const cur = doc.edges.find((e) => e.id === id);
  if (!cur) return doc;
  const next: FlowEdge = { ...cur };
  if (patch.source !== undefined) next.source = patch.source;
  if (patch.target !== undefined) next.target = patch.target;
  if (next.source === next.target) return doc;
  if (!doc.nodes.some((n) => n.id === next.source) || !doc.nodes.some((n) => n.id === next.target)) return doc;
  if (doc.edges.some((e) => e.id !== id && e.source === next.source && e.target === next.target)) return doc;
  if (patch.sourceHandle !== undefined) {
    if (patch.sourceHandle !== null && oneOf(FLOW_HANDLES, patch.sourceHandle)) next.sourceHandle = patch.sourceHandle;
    else delete next.sourceHandle;
  }
  if (patch.targetHandle !== undefined) {
    if (patch.targetHandle !== null && oneOf(FLOW_HANDLES, patch.targetHandle)) next.targetHandle = patch.targetHandle;
    else delete next.targetHandle;
  }
  if (patch.label !== undefined) {
    const l = patch.label === null ? undefined : cleanOptionalText(patch.label, FLOW_LIMITS.label);
    if (l === undefined) delete next.label;
    else next.label = l;
  }
  if (patch.kind !== undefined) {
    if (patch.kind !== null && oneOf(FLOW_EDGE_KINDS, patch.kind) && patch.kind !== "default") next.kind = patch.kind;
    else delete next.kind;
  }
  const canon = canonicalEdge(next);
  if (same(canon, cur)) return doc;
  return { ...doc, edges: doc.edges.map((e) => (e.id === id ? canon : e)) };
}

export function removeEdges(doc: FlowMapDoc, ids: Iterable<string>): FlowMapDoc {
  const drop = new Set(ids);
  if (!doc.edges.some((e) => drop.has(e.id))) return doc;
  return { ...doc, edges: doc.edges.filter((e) => !drop.has(e.id)) };
}

/** Change the map-level settings (direction, edge style). null removes the setting. */
export function updateFlowSettings(
  doc: FlowMapDoc,
  patch: { direction?: FlowDirection | null; edgeStyle?: FlowMapDoc["edgeStyle"] | null },
): FlowMapDoc {
  const next: FlowMapDoc = { ...doc };
  if (patch.direction !== undefined) {
    if (patch.direction !== null && oneOf(FLOW_DIRECTIONS, patch.direction)) next.direction = patch.direction;
    else delete next.direction;
  }
  if (patch.edgeStyle !== undefined) {
    if (patch.edgeStyle !== null && oneOf(FLOW_EDGE_STYLES, patch.edgeStyle)) next.edgeStyle = patch.edgeStyle;
    else delete next.edgeStyle;
  }
  if (next.direction === doc.direction && next.edgeStyle === doc.edgeStyle) return doc;
  return canonicalDoc(next);
}

/**
 * A three-node map to start from: start, one step, end. Hosts pass the titles in
 * their own language. Nodes share one centre line so the arrows run straight.
 */
export function starterFlowDoc(titles: { start: FlowText; step: FlowText; end: FlowText }, direction: FlowDirection = "TB"): FlowMapDoc {
  const S = FLOW_NODE_SIZE;
  const gap = 64;
  let pos: [FlowPoint, FlowPoint, FlowPoint];
  if (direction === "LR") {
    const cy = S.step.height / 2;
    const stepX = S.start.width + gap;
    pos = [
      { x: 0, y: cy - S.start.height / 2 },
      { x: stepX, y: 0 },
      { x: stepX + S.step.width + gap, y: cy - S.end.height / 2 },
    ];
  } else {
    const cx = S.step.width / 2;
    const stepY = S.start.height + gap;
    pos = [
      { x: cx - S.start.width / 2, y: 0 },
      { x: 0, y: stepY },
      { x: cx - S.end.width / 2, y: stepY + S.step.height + gap },
    ];
  }
  return normalizeFlowDoc({
    version: FLOW_DOC_VERSION,
    direction,
    nodes: [
      { id: "n1", kind: "start", position: pos[0], title: titles.start },
      { id: "n2", kind: "step", position: pos[1], title: titles.step },
      { id: "n3", kind: "end", position: pos[2], title: titles.end },
    ],
    edges: [
      { id: "e1", source: "n1", target: "n2" },
      { id: "e2", source: "n2", target: "n3" },
    ],
  });
}
